/**
 * Shared color palette → hex map.
 *
 * Why this exists:
 *   ProductCard.jsx and Catalog.jsx each had THEIR OWN partial color→hex
 *   map. Both were missing several DB colors and silently fell back to
 *   "#888" grey, which made the storefront swatch row + sidebar filter
 *   look like a single sad row of grey dots.
 *
 * All DB-active color names live here. Keep this file as the SINGLE
 * source of truth. When the admin adds a new colour name to a product,
 * append it below — please also pick a hex that's visually distinct
 * from siblings so swatches don't blur into each other.
 *
 * Naming: keys are case-insensitive (normalised in lookup); use the
 * canonical title-case form as the comment.
 */

const PALETTE = {
  // Neutrals
  black: "#111111",
  white: "#ffffff",
  grey: "#9ca3af",
  gray: "#9ca3af",
  charcoal: "#36454f",
  "heather grey": "#a8a8a8",
  "heather gray": "#a8a8a8",
  silver: "#cbd5e1",
  natural: "#e8dcc4",       // unbleached cotton
  cream: "#fef3c7",
  ivory: "#fffff0",
  beige: "#e7d7b0",
  tan: "#d2b48c",
  kraft: "#c2a878",         // recycled craft paper tone
  // Reds & pinks
  red: "#ef4444",
  crimson: "#dc143c",
  maroon: "#7f1d1d",
  burgundy: "#6b1e2d",
  rose: "#fb7185",
  pink: "#ec4899",
  "hot pink": "#ff1493",
  fuchsia: "#d946ef",
  coral: "#ff7f50",
  salmon: "#fa8072",
  // Oranges & yellows
  orange: "#FF5722",        // brand orange
  "burnt orange": "#cc4400",
  peach: "#ffb38a",
  yellow: "#facc15",
  mustard: "#d4a017",
  gold: "#d4af37",
  // Greens
  green: "#16a34a",
  "bottle green": "#006a4e",
  olive: "#65a30d",
  forest: "#1f513f",
  mint: "#a7f3d0",
  lime: "#84cc16",
  teal: "#14b8a6",
  // Blues
  blue: "#2563eb",
  "royal blue": "#1e40af",
  navy: "#0a1f44",          // deep navy, distinguishable from royal blue
  "sky blue": "#7dd3fc",
  "two-tone blue": "#3b82f6",
  cyan: "#06b6d4",
  turquoise: "#40e0d0",
  indigo: "#4f46e5",
  // Purples
  purple: "#a855f7",
  violet: "#8b5cf6",
  lavender: "#c4b5fd",
  // Browns
  brown: "#92400e",
  chocolate: "#5c2c0c",
};

/**
 * Look up the hex for a colour name. Returns a sensible fallback (medium
 * grey) instead of breaking the UI when the name is unknown.
 *
 * @param {string} name — colour name from product.available_colors
 * @returns {string} hex colour
 */
export function colorHex(name) {
  if (!name) return "#9ca3af";
  const n = String(name).trim().toLowerCase();
  if (PALETTE[n]) return PALETTE[n];
  // Multi-word colour normalisation: collapse extra spaces.
  const compact = n.replace(/\s+/g, " ");
  if (PALETTE[compact]) return PALETTE[compact];
  // Last-resort: pick the closest single-word base colour if the
  // name contains one. e.g. "Light Blue" → blue, "Dark Olive" → olive.
  for (const base of ["black", "white", "grey", "red", "orange", "yellow",
                      "green", "blue", "purple", "pink", "brown",
                      "navy", "olive", "teal", "indigo", "violet"]) {
    if (n.includes(base)) return PALETTE[base];
  }
  return "#9ca3af";
}

/**
 * For very pale / white-ish colours we need a visible border on the
 * swatch so it doesn't disappear against a white card background.
 */
export function colorNeedsBorder(name) {
  const hex = colorHex(name).toLowerCase();
  // Light backgrounds: white, ivory, cream, natural, beige, mint, lavender, peach, sky blue, heather grey
  return ["#ffffff", "#fffff0", "#fef3c7", "#e8dcc4", "#e7d7b0", "#a7f3d0",
          "#c4b5fd", "#ffb38a", "#7dd3fc", "#a8a8a8", "#cbd5e1"].includes(hex);
}
